/* Supabase client configuration for admin authentication and data operations
   Updated: Added documents permission for admin and superadmin roles
   Security update: the client now sends the visitor's cart session id as a request
   header (x-cart-session) so the database can keep each browser's cart private. */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase environment variables are not set');
}

export function getCartSessionId(): string {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return '';

  const stored = localStorage.getItem('cart_session_id');
  if (stored) return stored;

  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

  const newId = `session_${random}`;
  localStorage.setItem('cart_session_id', newId);
  return newId;
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    headers: {
      'x-cart-session': getCartSessionId(),
    },
  },
});

export type AdminRole = 'moderator' | 'admin' | 'superadmin';

export interface Admin {
  id: string;
  email: string;
  role: AdminRole;
  created_at: string;
  updated_at: string;
  last_login: string | null;
}

export const ROLE_PERMISSIONS = {
  moderator: ['blogs', 'products', 'assortiment', 'faq'],
  admin: ['blogs', 'products', 'assortiment', 'faq', 'mailing', 'settings', 'reservations', 'tours', 'gallery', 'coupons', 'orders', 'quote_requests', 'documents', 'business'],
  superadmin: ['blogs', 'blog_automation', 'products', 'assortiment', 'faq', 'mailing', 'admins', 'settings', 'reservations', 'tours', 'gallery', 'coupons', 'orders', 'quote_requests', 'documents', 'business'],
} as const;

export function hasAccess(role: AdminRole, module: string): boolean {
  return ROLE_PERMISSIONS[role].includes(module as any);
}
